/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, November 17th 2021, 10:04:18 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { StyledOSDialog, RightAlignContainer, Typography, OSLoadingButton } from "ui-widgets";
import { OSAPIForm, EmailTagsFormInput } from "ui-widgets/forms";
import { withAccessCheck } from "authorization/hoc";
import { useGlobalScope } from "core/providers";
import { SingleDepartmentSelectInput } from "organizations/components/DeparmentSelectorInputs";
import { PortalsCheckboxesInput } from "portals/components/PortalSelectorInputs";
import { WorkerTypeDropDownInput } from "workers";
import { COLORSCHEME } from "theme/styles";
import { UserInvitation } from "lib/schemas";
import { Stack, Box } from "@mui/material";
import { useInvitationAPI } from "onboarding/services/InvitationAPI";
import { TeamsCheckboxesInput } from "teams/components/TeamSelectorInputs";
import { UserPlus } from "react-feather";

export const InviteWorkersDialog = ({ open, handleClose }) => {
  return (
    <StyledOSDialog
      popUpIcon={<UserPlus color={COLORSCHEME.primaryVariant} />}
      popUpTitle="Invite New Workers"
      open={open}
      onClose={handleClose}
    >
      <InviteWorkersForm />
    </StyledOSDialog>
  );
};

const InviteWorkersForm = withAccessCheck({ resourceId: "invite-workers" }, () => {
  const { globalState } = useGlobalScope();

  const { useInviteWorkersAPIForm } = useInvitationAPI();

  const { useAPIFormReturn } = useInviteWorkersAPIForm();
  return (
    <OSAPIForm className="os-modal-invite-workers" showSubmitButton={false} useAPIFormReturn={useAPIFormReturn}>
      <Stack spacing={0}>
        <Box sx={{ paddingLeft: 0, paddingRight: 0, paddingTop: 0 }}>
          <Stack spacing={0}>
            <Typography variant="h6" className="os-content-description">
              {/* Bring your co-workers onboard to receive, review and process online submissions for{" "}
              <b>{globalState.selectedOrganization?.name}</b> services. */}
              Bring your co-workers at <b>{globalState.selectedOrganization?.name}</b> onboard.
            </Typography>

            <div className="os-modal-iw-field">
              <Typography variant="subtitle2" className="os-invite-worker-label">
                Enter Email Addresses
              </Typography>
              <EmailTagsFormInput<UserInvitation>
                schema_field_key="invitee_emails"
                fullWidth
                placeholder="Enter the email address of the worker to invite"
              />
            </div>

            <div className="os-modal-iw-field">
              <Typography variant="subtitle2" className="os-invite-worker-label">
                Select Users' Deparment
              </Typography>
              <SingleDepartmentSelectInput<UserInvitation> schema_field_key="department" label="" />
            </div>

            <div className="os-modal-iw-field">
              <Typography variant="subtitle2" className="os-invite-worker-label">
                Invite as:
              </Typography>
              <WorkerTypeDropDownInput<UserInvitation> schema_field_key="invited_as" />
            </div>

            <div className="os-modal-iw-field">
              <Typography variant="subtitle2" className="os-invite-worker-label">
                Select Users' Portal(s)
              </Typography>
              <div className="os-input-action-button-right-wrapper">
                <PortalsCheckboxesInput<UserInvitation> schema_field_key="portals" />
              </div>
            </div>

            <div className="os-modal-iw-field">
              <Typography variant="subtitle2" className="os-invite-worker-label">
                Add User to Teams (Optional)
              </Typography>
              <div className="os-input-action-button-right-wrapper">
                <TeamsCheckboxesInput<UserInvitation> schema_field_key="invitee_teams" />
              </div>
            </div>
          </Stack>
        </Box>

        <RightAlignContainer>
          <OSLoadingButton
            className="modal-button-A"
            size="large"
            variant="contained"
            isLoading={useAPIFormReturn.isSendingDataToAPI}
            type="submit"
          >
            Invite
          </OSLoadingButton>
        </RightAlignContainer>
      </Stack>
    </OSAPIForm>
  );
});
